import { WebhostingSection } from "@/components/customers/webhosting-section";
import { ProjectDot } from "@/components/projects/project-color";
import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LoadingLabel } from "@/components/ui/loading-label";
import { LocalDate } from "@/components/ui/local-date";
import { toast } from "@/components/ui/use-toast";
import { ApiError, api } from "@/lib/api";
import { useDocumentTitle } from "@/lib/document-title";
import { useApiMutation, useRowMutation } from "@/lib/use-api-mutation";
import { formatCurrency } from "@/lib/utils";
import { Link, createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";

/**
 * `/customers/:id` — accountant/admin-only customer profile: a
 * read-only overview with headline statistics, the customer's
 * projects, billing costs and invoice batches. Editing the
 * customer's own fields (and deleting it) lives on the separate
 * `/customers/:id/edit` page.
 */

const batchStatusVariant: Record<
  string,
  "default" | "secondary" | "success" | "warning" | "destructive" | "outline"
> = {
  draft: "outline",
  processing: "warning",
  sent: "default",
  paid: "success",
  failed: "destructive",
};

type CustomerCost = {
  id: string;
  label: string;
  amount: string;
  consumedByBatchId: string | null;
  createdAt: string;
};

type StateBucket = { hours: number; amount: number; count: number };
type ApprovalState = "worked" | "budget" | "approved" | "paid" | "rejected";
type BillingState =
  | "unbilled"
  | "to_invoice"
  | "invoiced"
  | "paid"
  | "non_billable"
  | "fixed_price";
type TEStats = {
  byApproval: Record<ApprovalState, StateBucket>;
  byBilling: Record<BillingState, StateBucket>;
  total: StateBucket;
};

type CustomerDetail = {
  id: string;
  name: string;
  fakturoidSubjectId: string | null;
  /** The accounting contact this customer bills through, when linked. */
  contact: { id: string; name: string } | null;
  createdAt: string;
  projects: Array<{
    id: string;
    name: string;
    gitlabProjectId: string;
    _count: { workItems: number };
  }>;
  invoiceBatches: Array<{
    id: string;
    periodFrom: string;
    periodTo: string;
    status: string;
    fakturoidInvoiceNumber: string | null;
    totalAmount: string | null;
    createdAt: string;
  }>;
  costs: CustomerCost[];
  invoices: Array<{
    id: string;
    type: string;
    number: string | null;
    status: string;
    issuedOn: string | null;
    dueOn: string | null;
    paidAt: string | null;
    total: string | null;
    currency: string;
  }>;
  teStats: TEStats;
};

const invoiceStatusVariant: Record<
  string,
  "default" | "secondary" | "success" | "warning" | "destructive" | "outline"
> = {
  issued: "default",
  partially_paid: "warning",
  overdue: "destructive",
  paid: "success",
  cancelled: "secondary",
};

export const Route = createFileRoute("/_app/customers/$id/")({
  loader: async ({ params }) => {
    try {
      return await api.get<CustomerDetail>(`/api/customers/${params.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: CustomerProfilePage,
});

function CustomerProfilePage() {
  const customer = Route.useLoaderData();
  useDocumentTitle(customer.name);

  // Client-sorted DataTables — the columns' `sortAccessor`s drive the
  // ordering, no external sort state needed.
  const projectColumns: Array<DataTableColumn<CustomerDetail["projects"][number]>> = [
    {
      key: "name",
      header: "Name",
      sortAccessor: (p) => p.name,
      cellClassName: "font-medium",
      cell: (p) => (
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <ProjectDot projectId={p.id} />
          <AppLink href={`/projects/${p.id}`} className="truncate hover:underline text-primary">
            {p.name}
          </AppLink>
        </span>
      ),
    },
    {
      key: "gitlabProjectId",
      header: "GitLab ID",
      sortAccessor: (p) => p.gitlabProjectId,
      cell: (p) => (
        <Badge variant="outline" className="font-mono text-xs">
          {p.gitlabProjectId}
        </Badge>
      ),
    },
    {
      key: "workItems",
      header: "Work Items",
      sortAccessor: (p) => p._count.workItems,
      cell: (p) => p._count.workItems,
    },
  ];

  const batchColumns: Array<DataTableColumn<CustomerDetail["invoiceBatches"][number]>> = [
    {
      key: "period",
      header: "Period",
      sortAccessor: (b) => b.periodFrom,
      cellClassName: "text-sm",
      cell: (b) => (
        <>
          <LocalDate value={b.periodFrom} /> – <LocalDate value={b.periodTo} />
        </>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortAccessor: (b) => b.status,
      cell: (b) => <Badge variant={batchStatusVariant[b.status] ?? "outline"}>{b.status}</Badge>,
    },
    {
      key: "invoiceNumber",
      header: "Invoice #",
      sortAccessor: (b) => b.fakturoidInvoiceNumber,
      cellClassName: "font-mono text-xs",
      cell: (b) => b.fakturoidInvoiceNumber ?? "—",
    },
    {
      key: "amount",
      header: "Amount",
      sortAccessor: (b) => (b.totalAmount === null ? null : Number(b.totalAmount)),
      cell: (b) => (b.totalAmount ? formatCurrency(Number(b.totalAmount)) : "—"),
    },
    {
      key: "createdAt",
      header: "Created",
      sortAccessor: (b) => b.createdAt,
      cellClassName: "text-sm text-muted-foreground",
      cell: (b) => <LocalDate value={b.createdAt} />,
    },
  ];

  const invoiceColumns: Array<DataTableColumn<CustomerDetail["invoices"][number]>> = [
    {
      key: "number",
      header: "Invoice #",
      sortAccessor: (i) => i.number,
      cellClassName: "font-mono text-xs",
      cell: (i) => (
        <Link to="/accounting/invoices/$id" params={{ id: i.id }} className="hover:underline">
          {i.number ?? "—"}
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortAccessor: (i) => i.status,
      cell: (i) => <Badge variant={invoiceStatusVariant[i.status] ?? "outline"}>{i.status}</Badge>,
    },
    {
      key: "issuedOn",
      header: "Issued",
      sortAccessor: (i) => i.issuedOn,
      cellClassName: "text-sm",
      cell: (i) => (i.issuedOn ? <LocalDate value={i.issuedOn} /> : "—"),
    },
    {
      key: "dueOn",
      header: "Due",
      sortAccessor: (i) => i.dueOn,
      cellClassName: "text-sm",
      cell: (i) => (i.dueOn ? <LocalDate value={i.dueOn} /> : "—"),
    },
    {
      key: "paidAt",
      header: "Paid",
      sortAccessor: (i) => i.paidAt,
      cellClassName: "text-sm text-muted-foreground",
      cell: (i) => (i.paidAt ? <LocalDate value={i.paidAt} /> : "—"),
    },
    {
      key: "total",
      header: "Total",
      sortAccessor: (i) => (i.total === null ? null : Number(i.total)),
      cell: (i) => (i.total ? formatCurrency(Number(i.total), i.currency) : "—"),
    },
  ];

  // Headline statistics, derived from the detail payload.
  const workItemCount = customer.projects.reduce((s, p) => s + p._count.workItems, 0);
  const totalPaid = customer.invoiceBatches
    .filter((b) => b.status === "paid")
    .reduce((s, b) => s + Number(b.totalAmount ?? 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">{customer.name}</h1>
          <p className="text-muted-foreground mt-1">
            Customer profile
            {customer.contact && (
              <>
                {" · bills through "}
                <Link
                  to="/accounting/contacts/$id"
                  params={{ id: customer.contact.id }}
                  className="underline"
                >
                  {customer.contact.name}
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild>
            <Link to="/customers/$id/edit" params={{ id: customer.id }}>
              Edit customer
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <AppLink href="/customers">Back to Customers</AppLink>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard label="Projects" value={String(customer.projects.length)} />
        <StatCard label="Work items" value={String(workItemCount)} />
        <StatCard label="Invoice batches" value={String(customer.invoiceBatches.length)} />
        <StatCard label="Total paid" value={formatCurrency(totalPaid)} />
      </div>

      <TEStatsCard stats={customer.teStats} />

      <Card>
        <CardHeader>
          <CardTitle>Projects</CardTitle>
          <CardDescription>{customer.projects.length} projects</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={projectColumns}
            rows={customer.projects}
            rowKey={(p) => p.id}
            emptyState="No projects yet."
          />
        </CardContent>
      </Card>

      <WebhostingSection customerId={customer.id} />

      <Card>
        <CardHeader>
          <CardTitle>Billing Costs</CardTitle>
          <CardDescription>
            One-off costs picked per report and consumed once invoiced. (Recurring costs like
            webhosting are managed in the section above.)
          </CardDescription>
        </CardHeader>
        <CardContent>
          <CustomerCostsSection customerId={customer.id} costs={customer.costs} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Invoice Batches</CardTitle>
          <CardDescription>{customer.invoiceBatches.length} batches</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={batchColumns}
            rows={customer.invoiceBatches}
            rowKey={(b) => b.id}
            emptyState="No invoice batches yet."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Invoices</CardTitle>
          <CardDescription>
            {customer.invoices.length} invoice{customer.invoices.length === 1 ? "" : "s"} in the
            accounting
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={invoiceColumns}
            rows={customer.invoices}
            rowKey={(i) => i.id}
            emptyState="No invoices issued to this customer yet."
          />
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-bold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

const approvalLabel: Record<ApprovalState, string> = {
  worked: "Worked",
  budget: "Budget",
  approved: "Approved",
  paid: "Paid out",
  rejected: "Rejected",
};
const billingLabel: Record<BillingState, string> = {
  unbilled: "Unbilled",
  to_invoice: "To invoice",
  invoiced: "Invoiced",
  paid: "Invoice paid",
  non_billable: "Non-billable",
  // Amount here is the contracted fixed price of the work items these
  // rows belong to, not hours × rate.
  fixed_price: "Fixed price",
};

/**
 * Two side-by-side breakdowns of the customer's TE volume — by
 * employee-side approval state on the left, customer-side billing
 * state on the right. Each cell shows count, hours and computed
 * amount (snapshotRate when set, else the live billing-rate
 * resolver per (project, user, spentOn)).
 */
function TEStatsCard({ stats }: { stats: TEStats }) {
  const approvalKeys: ApprovalState[] = ["worked", "budget", "approved", "paid", "rejected"];
  const billingKeys: BillingState[] = [
    "unbilled",
    "to_invoice",
    "fixed_price",
    "invoiced",
    "paid",
    "non_billable",
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle>Time Entries</CardTitle>
        <CardDescription>
          {stats.total.count} entries · {stats.total.hours.toFixed(2)} h ·{" "}
          {formatCurrency(stats.total.amount)} total
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <StatTable
            title="By status (employee)"
            rows={approvalKeys.map((k) => ({
              label: approvalLabel[k],
              bucket: stats.byApproval[k],
            }))}
          />
          <StatTable
            title="By billing (customer)"
            rows={billingKeys.map((k) => ({ label: billingLabel[k], bucket: stats.byBilling[k] }))}
          />
        </div>
      </CardContent>
    </Card>
  );
}

type StatRow = { label: string; bucket: StateBucket };

const statColumns: Array<DataTableColumn<StatRow>> = [
  { key: "state", header: "State", cell: (r) => r.label },
  {
    key: "entries",
    header: "Entries",
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (r) => r.bucket.count,
  },
  {
    key: "hours",
    header: "Hours",
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (r) => r.bucket.hours.toFixed(2),
  },
  {
    key: "amount",
    header: "Amount",
    headClassName: "text-right",
    cellClassName: "text-right tabular-nums",
    cell: (r) => formatCurrency(r.bucket.amount),
  },
];

function StatTable({ title, rows }: { title: string; rows: StatRow[] }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-muted-foreground">{title}</h3>
      <DataTable columns={statColumns} rows={rows} rowKey={(r) => r.label} />
    </div>
  );
}

function CustomerCostsSection({
  customerId,
  costs,
}: {
  customerId: string;
  costs: CustomerCost[];
}) {
  const router = useRouter();
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const addCost = useApiMutation(
    (body: { label: string; amount: number }) =>
      api.post(`/api/customers/${customerId}/costs`, body),
    {
      onSuccess: () => {
        toast({ title: "Cost added", variant: "success" });
        setLabel("");
        setAmount("");
        router.invalidate();
      },
      onError: (msg) => toast({ title: "Add failed", description: msg, variant: "destructive" }),
    },
  );
  const removeCost = useRowMutation(
    (cost: CustomerCost) => api.delete(`/api/customers/${customerId}/costs/${cost.id}`),
    {
      rowKey: (cost) => cost.id,
      onSuccess: () => {
        toast({ title: "Cost removed", variant: "success" });
        router.invalidate();
      },
      onError: (msg) => toast({ title: "Delete failed", description: msg, variant: "destructive" }),
    },
  );

  async function submitCost(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const amountNum = Number(amount);
    if (!label.trim() || !Number.isFinite(amountNum) || amountNum <= 0) {
      toast({ title: "Label and a positive amount are required", variant: "destructive" });
      return;
    }
    await addCost.mutate({ label: label.trim(), amount: amountNum });
  }

  const fieldClass =
    "flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <div className="space-y-4">
      <form onSubmit={submitCost} className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <label htmlFor="cost-label" className="text-sm font-medium leading-none">
            Label
          </label>
          <input
            id="cost-label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Webhosting"
            className={`${fieldClass} w-48`}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="cost-amount" className="text-sm font-medium leading-none">
            Amount (CZK)
          </label>
          <input
            id="cost-amount"
            type="number"
            step="0.01"
            min="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="500"
            className={`${fieldClass} w-32`}
          />
        </div>
        <Button type="submit" disabled={addCost.pending}>
          {addCost.pending ? <LoadingLabel text="Adding" /> : "Add cost"}
        </Button>
      </form>

      <DataTable
        columns={[
          {
            key: "label",
            header: "Label",
            cellClassName: "font-medium",
            cell: (cost) => cost.label,
          },
          {
            key: "amount",
            header: "Amount",
            cell: (cost) => formatCurrency(Number(cost.amount)),
          },
          {
            key: "state",
            header: "State",
            cell: (cost) =>
              cost.consumedByBatchId ? (
                <Badge variant="secondary">Invoiced</Badge>
              ) : (
                <Badge variant="outline">Pending</Badge>
              ),
          },
          {
            key: "actions",
            header: null,
            cellClassName: "text-right",
            cell: (cost) => (
              <Button variant="outline" size="sm" onClick={() => removeCost.run(cost)}>
                Remove
              </Button>
            ),
          },
        ]}
        rows={costs}
        rowKey={(cost) => cost.id}
        emptyState="No billing costs configured."
      />
    </div>
  );
}
